import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SITUACOES_CONHECIDAS, type Convenio } from "@/api/contrato";
import {
  useConvenio,
  useConvenios,
  usePessoa,
  usePessoas,
  useSistemas,
  type FiltrosDeBoletos as FiltrosDaConsulta,
} from "@/api/consultas";
import { Campo } from "@/componentes/Campo";
import { CampoDeBusca } from "@/componentes/CampoDeBusca";
import { ChipsDeFiltro } from "@/componentes/ChipsDeFiltro";
import { Dica } from "@/componentes/Dica";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { Selecao } from "@/componentes/Selecao";
import {
  useFiltrosNaUrl,
  type DefinicaoDeFiltro,
  type FiltrosNaUrl,
} from "@/componentes/useFiltrosNaUrl";
import { banco, dia, dinheiro, idCurto } from "@/formato";
import { useSessao } from "@/sessao/useSessao";
import { FiltroDeBanco } from "@/telas/cedentes/ListaDeCedentes";
import { centavosDe } from "@/telas/planos/plano";
import { OPCOES_DE_ENVIO, rotuloDoEnvio } from "./envio";

/*
 * Os filtros de Boletos: todo filtro da API tem controle, e todo filtro aplicado vira
 * pílula (spec `tela-de-boletos`).
 *
 * Os controles ficam recolhidos, como na faixa "Filtros" da GUI da TecnoSpeed: a tabela é
 * o trabalho, e o filtro aplicado continua à vista na pílula.
 *
 * A URL guarda o que o operador escolheu, e `consulta` é o que vai à API: valor em
 * centavos e o dia de criação como instante. A lista e o CSV leem a mesma `consulta`, e
 * por isso o arquivo traz o conjunto da tela.
 */
type ChaveDoFiltro = Exclude<keyof FiltrosDaConsulta, "antes">;
export type Opcoes = readonly (readonly [valor: string, rotulo: string])[];

export interface FiltrosDaTela extends FiltrosNaUrl<ChaveDoFiltro> {
  consulta: Omit<FiltrosDaConsulta, "antes">;
  opcoes: { sistemas: Opcoes; pessoas: Opcoes; convenios: Opcoes };
}

const ESPERA_DA_DIGITACAO_MS = 300;

/** O nome das opções que dependem do cadastro. */
interface Nomes {
  aplicacao: (id: string) => string;
  cedente: (id: string) => string;
  convenio: (id: string) => string;
}

function definicoes(nomes: Nomes): readonly DefinicaoDeFiltro<ChaveDoFiltro>[] {
  return [
    { chave: "q", rotulo: "Busca" },
    { chave: "situacao", rotulo: "Situação" },
    { chave: "banco", rotulo: "Banco", textoDoValor: banco },
    { chave: "sistema_id", rotulo: "Aplicação", textoDoValor: nomes.aplicacao },
    { chave: "pessoa_id", rotulo: "Cedente", textoDoValor: nomes.cedente },
    { chave: "convenio_id", rotulo: "Convênio", textoDoValor: nomes.convenio },
    { chave: "vence_de", rotulo: "Vence de", textoDoValor: dia },
    { chave: "vence_ate", rotulo: "Vence até", textoDoValor: dia },
    { chave: "criado_de", rotulo: "Criado de", textoDoValor: dia },
    { chave: "criado_ate", rotulo: "Criado até", textoDoValor: dia },
    { chave: "valor_minimo", rotulo: "Valor mínimo", textoDoValor: reais },
    { chave: "valor_maximo", rotulo: "Valor máximo", textoDoValor: reais },
    { chave: "indeterminado", rotulo: "Indeterminados", textoDoValor: simOuNao },
    { chave: "presos", rotulo: "Presos", textoDoValor: simOuNao },
    { chave: "envio_em_aberto", rotulo: "Envio em aberto", textoDoValor: simOuNao },
    { chave: "envio_estado", rotulo: "Envio", textoDoValor: rotuloDoEnvio },
  ];
}

function reais(centavos: string): string {
  const valor = Number(centavos);
  return Number.isSafeInteger(valor) ? dinheiro(valor) : centavos;
}

function simOuNao(valor: string): string {
  return valor === "true" ? "sim" : "não";
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `criado_em` É INSTANTE, E A API LÊ DATA PURA COMO MEIA-NOITE EM UTC.     │
 * │                                                                          │
 * │ `criado_ate=2026-10-15` cortaria o dia 15 inteiro, e `criado_de` traria  │
 * │ as 21 h do dia 14 no fuso de Brasília. Por isso o dia escolhido sai como │
 * │ o dia inteiro, no fuso do operador. `vencimento` é `date`, e passa como  │
 * │ veio. Instante completo, ou texto torto que a API recusa com `400`,      │
 * │ também: quem julga parâmetro é ela.                                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function instanteDoDia(valor: string | undefined, hora: string): string | undefined {
  if (valor === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;

  const instante = new Date(`${valor}T${hora}`);
  return Number.isNaN(instante.getTime()) ? valor : instante.toISOString();
}

export function nomeNaLista(opcoes: Opcoes, id: string): string {
  return opcoes.find(([valor]) => valor === id)?.[1] ?? idCurto(id);
}

function nomeDoConvenio(convenio: Convenio): string {
  return `${convenio.banco} · ${convenio.numero} · ${convenio.pessoa_nome}`;
}

export function useFiltrosDeBoletos(): FiltrosDaTela {
  const { cliente, estado } = useSessao();
  const [parametros] = useSearchParams();
  const dentro = estado.fase === "dentro";

  const { data: sistemas } = useSistemas(cliente, dentro);
  const { data: pessoas } = usePessoas(cliente, dentro);
  const { data: convenios } = useConvenios(cliente, dentro);

  /*
   * As listas param no teto de 200, e o link da aba Boletos do cedente pode trazer o de
   * número 201. O nome vem da leitura por `id`, e a lista só cobre a falha dela.
   */
  const { data: cedente } = usePessoa(cliente, parametros.get("pessoa_id") || null);
  const { data: convenio } = useConvenio(cliente, parametros.get("convenio_id") || null);

  const opcoes = {
    sistemas: (sistemas?.sistemas ?? []).map((item) => [item.id, item.nome] as const),
    pessoas: (pessoas?.pessoas ?? []).map((item) => [item.id, item.razao_social] as const),
    convenios: (convenios?.convenios ?? []).map(
      (item) => [item.id, nomeDoConvenio(item)] as const,
    ),
  };

  const filtros = useFiltrosNaUrl(
    definicoes({
      aplicacao: (id) => nomeNaLista(opcoes.sistemas, id),
      cedente: (id) => cedente?.razao_social ?? nomeNaLista(opcoes.pessoas, id),
      convenio: (id) =>
        convenio === undefined
          ? nomeNaLista(opcoes.convenios, id)
          : nomeDoConvenio(convenio.convenio),
    }),
  );

  return {
    ...filtros,
    opcoes,
    consulta: {
      ...filtros.valores,
      criado_de: instanteDoDia(filtros.valores.criado_de, "00:00:00"),
      criado_ate: instanteDoDia(filtros.valores.criado_ate, "23:59:59.999"),
    },
  };
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O CAMPO MOSTRA O DIGITADO, E A URL RECEBE A CÓPIA 300 MS DEPOIS.         │
 * │                                                                          │
 * │ `digitado` fica de pé até a URL mudar por outro caminho: pílula, voltar  │
 * │ do navegador ou busca global. Sem ele, a URL da tecla anterior chegaria  │
 * │ depois da tecla seguinte e a caixa comeria o caractere. `enviado` é o    │
 * │ que separa as duas chegadas.                                             │
 * │                                                                          │
 * │ `aplicar` entra por `ref`: a função da tecla navega a partir da rota     │
 * │ daquele render, e um boleto aberto durante a espera fecharia sozinho.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function useTextoComEspera(daUrl: string, aplicar: (valor: string) => void) {
  const [digitado, setDigitado] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<string | null>(null);
  const [vista, setVista] = useState(daUrl);

  if (daUrl !== vista) {
    setVista(daUrl);
    if (daUrl !== enviado) setDigitado(null);
  }

  const aplicarAtual = useRef(aplicar);
  useEffect(() => {
    aplicarAtual.current = aplicar;
  });

  useEffect(() => {
    if (digitado === null || digitado === daUrl) return;

    const espera = setTimeout(() => {
      setEnviado(digitado);
      aplicarAtual.current(digitado);
    }, ESPERA_DA_DIGITACAO_MS);

    return () => clearTimeout(espera);
  }, [digitado, daUrl]);

  return { valor: digitado ?? daUrl, pedir: setDigitado };
}

function BuscaDeBoletos({ filtros }: { filtros: FiltrosDaTela }) {
  const { valor, pedir } = useTextoComEspera(filtros.valores.q ?? "", (termo) =>
    filtros.ajustar("q", termo),
  );

  return (
    <div className="flex flex-wrap items-end gap-2">
      <CampoDeBusca
        rotulo="Buscar boleto"
        placeholder="Referência, nosso número, documento do sacado, id"
        valor={valor}
        aoMudar={pedir}
      />
    </div>
  );
}

/** `"123456"` → `"1234,56"`. Sem ponto flutuante: a conta é sobre os dígitos. */
function paraOCampo(centavos: string): string {
  if (!/^\d+$/.test(centavos)) return "";

  const digitos = centavos.padStart(3, "0");
  return `${digitos.slice(0, -2)},${digitos.slice(-2)}`;
}

/** Vazio tira o filtro, e `null` é texto que não vira valor. O `12,` é digitação em curso. */
function centavosDoCampo(texto: string): string | null {
  const limpo = texto.trim().replace(/[.,]$/, "");
  if (limpo === "") return "";

  const centavos = centavosDe(limpo);
  if (centavos === null || centavos < 0 || !Number.isSafeInteger(centavos)) return null;
  return String(centavos);
}

/** O texto fica aqui, como em `CampoDeBusca`: `12,5` não pode virar `12,50` sob o cursor. */
function CampoDeReais({
  filtros,
  chave,
  rotulo,
}: {
  filtros: FiltrosDaTela;
  chave: "valor_minimo" | "valor_maximo";
  rotulo: string;
}) {
  const centavos = filtros.valores[chave] ?? "";
  const { valor, pedir } = useTextoComEspera(centavos, (proximo) =>
    filtros.ajustar(chave, proximo),
  );

  const [texto, setTexto] = useState(() => paraOCampo(centavos));
  const [ultimo, setUltimo] = useState(valor);

  if (valor !== ultimo) {
    setUltimo(valor);
    if (valor !== centavosDoCampo(texto)) setTexto(paraOCampo(valor));
  }

  return (
    <Campo
      rotulo={rotulo}
      inputMode="decimal"
      value={texto}
      erro={centavosDoCampo(texto) === null ? "Use um valor como 29,90." : undefined}
      onChange={(evento) => {
        setTexto(evento.target.value);
        const proximo = centavosDoCampo(evento.target.value);
        if (proximo !== null) pedir(proximo);
      }}
      className="h-8! w-36"
    />
  );
}

export function CampoDeDia<C extends string>({
  filtros,
  chave,
  rotulo,
}: {
  filtros: Pick<FiltrosNaUrl<C>, "valores" | "ajustar">;
  chave: C;
  rotulo: string;
}) {
  return (
    <Campo
      rotulo={rotulo}
      type="date"
      value={filtros.valores[chave]?.slice(0, 10) ?? ""}
      onChange={(evento) => filtros.ajustar(chave, evento.target.value)}
      className="h-8! w-36"
    />
  );
}

/**
 * A seleção de um filtro. O valor que veio por link pode estar fora da lista, e sem a
 * opção dele o `select` diria "Todos" com o filtro aplicado.
 */
export function Escolha<C extends string>({
  filtros,
  chave,
  rotulo,
  todas,
  opcoes,
}: {
  filtros: Pick<FiltrosNaUrl<C>, "valores" | "aplicados" | "ajustar">;
  chave: C;
  rotulo: string;
  todas: string;
  opcoes: Opcoes;
}) {
  const valor = filtros.valores[chave];
  const foraDaLista = valor !== undefined && !opcoes.some(([opcao]) => opcao === valor);
  const texto = filtros.aplicados.find((filtro) => filtro.chave === chave)?.texto;

  return (
    <Selecao
      rotulo={rotulo}
      opcoes={[
        ["", todas],
        ...(foraDaLista ? [[valor, texto ?? valor] as const] : []),
        ...opcoes,
      ]}
      value={valor ?? ""}
      onChange={(evento) => filtros.ajustar(chave, evento.target.value)}
      className="h-8! w-52"
    />
  );
}

function Marca({
  filtros,
  chave,
  rotulo,
  dica,
}: {
  filtros: FiltrosDaTela;
  chave: "indeterminado" | "presos" | "envio_em_aberto";
  rotulo: string;
  dica: string;
}) {
  return (
    <span className="flex h-8 items-center gap-1">
      <label className="flex items-center gap-2 text-corpo text-texto-2">
        <input
          type="checkbox"
          checked={filtros.valores[chave] === "true"}
          onChange={(evento) => filtros.ajustar(chave, evento.target.checked ? "true" : "")}
        />
        {rotulo}
      </label>
      <Dica texto={dica} />
    </span>
  );
}

const SITUACOES: Opcoes = SITUACOES_CONHECIDAS.map((situacao) => [situacao, situacao] as const);

export function FiltrosDeBoletos({ filtros }: { filtros: FiltrosDaTela }) {
  return (
    <>
      <BuscaDeBoletos filtros={filtros} />

      <PainelRecolhivel titulo="Filtros" abertoAoIniciar={false}>
        <div className="flex flex-wrap items-start gap-2">
          <Escolha
            filtros={filtros}
            chave="situacao"
            rotulo="Situação"
            todas="Todas"
            opcoes={SITUACOES}
          />
          <FiltroDeBanco
            valor={filtros.valores.banco}
            aoMudar={(valor) => filtros.ajustar("banco", valor)}
          />
          <Escolha
            filtros={filtros}
            chave="sistema_id"
            rotulo="Aplicação"
            todas="Todas"
            opcoes={filtros.opcoes.sistemas}
          />
          <Escolha
            filtros={filtros}
            chave="pessoa_id"
            rotulo="Cedente"
            todas="Todos"
            opcoes={filtros.opcoes.pessoas}
          />
          <Escolha
            filtros={filtros}
            chave="convenio_id"
            rotulo="Convênio"
            todas="Todos"
            opcoes={filtros.opcoes.convenios}
          />
          <Escolha
            filtros={filtros}
            chave="envio_estado"
            rotulo="Envio"
            todas="Todos"
            opcoes={OPCOES_DE_ENVIO}
          />
        </div>

        <div className="flex flex-wrap items-start gap-2">
          <CampoDeDia filtros={filtros} chave="vence_de" rotulo="Vence de" />
          <CampoDeDia filtros={filtros} chave="vence_ate" rotulo="Vence até" />
          <CampoDeDia filtros={filtros} chave="criado_de" rotulo="Criado de" />
          <CampoDeDia filtros={filtros} chave="criado_ate" rotulo="Criado até" />
          <CampoDeReais filtros={filtros} chave="valor_minimo" rotulo="Valor mínimo (R$)" />
          <CampoDeReais filtros={filtros} chave="valor_maximo" rotulo="Valor máximo (R$)" />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Marca
            filtros={filtros}
            chave="indeterminado"
            rotulo="Indeterminados"
            dica="96 h sem confirmação do provider, e sem resolução manual."
          />
          <Marca
            filtros={filtros}
            chave="presos"
            rotulo="Presos"
            dica="Em RECEBIDO ou ENFILEIRADO há mais de 45 minutos, sem ninguém responsável por eles."
          />
          <Marca
            filtros={filtros}
            chave="envio_em_aberto"
            rotulo="Envio em aberto"
            dica="Sem desfecho depois da chamada ao provider: pode existir título, e quem responde é a sonda."
          />
        </div>
      </PainelRecolhivel>

      <ChipsDeFiltro filtros={filtros} />
    </>
  );
}
